import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Select,Spin, Typography } from "antd";

// libraries
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowDown } from "react-icons/fa";

import { message } from "antd";

// components
import ChatSkeleton from "../../component/Chat/ChatSkeleton";
import PromptTemplatesIntro from "../../component/ChatPage/PromptTemplatesIntro";
import ChatPromptInputForm from "../../component/ChatPage/ChatPromptInputForm";
import MessageContainer from "../../component/Chat/MessageContainer";

// hooks & contexts
import useChatPage from "../../Hooks/useChatPage";
import { PromptTemplateContext } from "../../contexts/PromptTemplateContext";
import { SidebarContext } from "../../contexts/SidebarContext";

// services & helpers
import WarningModal from '../../Common/Modal/WarningModal';
import useRemaining from '../../Hooks/useRemaining';
import {
  generateThreadId,
  getIdsFromItems,
  getItemsFromIds,
  inputElementAutoGrow,
  scrollToBottomForRefElement,
} from "../../Utility/chat-page-helper";
import { getGptResponse } from "../../api/chat-page-api";
import useSocket from "../../Hooks/useSocket";
import { CHAT_EVENTS } from "../../constants/sockets/chat";
import { userSubscriptionInfo } from "../../api/profile";

// api

const ChatPage = () => {
  const { thread_id } = useParams();
  // ----- STATES ----- //
  const [inputPrompt, setInputPrompt] = useState("");
  const [selectedChatModel, setSelectedChatModel] = useState("openai");
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(true);
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [showQMassage, setShowQMassage] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [fetching, setFetching] = useState(true);

  // ----- REFS ----- //
  const chatLogWrapperRef = useRef(null);
  const promptInputRef = useRef(null);
  const cancelTokenSourceRef = useRef();
  const timeOutIds = [];

  // ----- HOOK & CONTEXT VARIABLES ----- //
  const { currentPromptTemplate } = useContext(PromptTemplateContext);
  const {
    chatLog,
    templateCategories,
    tagList,
    errorMessage,
    isFetchingChatLog,
    isFirstMessage,
    isGeneratingResponse,
    setChatLog,
    setErrorMessage,
    setIsFirstMessage,
    setIsGeneratingResponse,
    fetchChatLogPerThread,
    fetchTagList,
    fetchTemplates,
    updateLastPrompt,
  } = useChatPage();

  const [assistantId,setAssistantId] = useState(null);

  useEffect(() => {
    const url = window.location.href;
    const regex = /agents\/(asst_[A-Za-z0-9]+)/;
    const match = url.match(regex);
    if (match) {
      setAssistantId(match[1]);
    }
  }, []);


  const navigate = useNavigate();
  const { setTriggerNavContent } = useContext(SidebarContext);
  // Get a ref to the socket instance
  const chatSocketRef = useSocket(CHAT_EVENTS.CHAT_NAMESPACE);
  const { userInfo, remaining, expireDate } = useRemaining();
  const [remainingToken,setRemainingToken] = useState(true);

  //----- Edit last Prompt --------//
  const emitUpdateLastPrompt = async (payload, promptId, newPrompt) => {
    const payloadData = {
      ...payload,
      chatLog,
    };

    const chatSocket = chatSocketRef.current;

    if (chatSocket) {
      chatSocket.emit(CHAT_EVENTS.EDIT_LAST_CHAT, payloadData);
    } else {
      const { success, data } = await updateLastPrompt(promptId, newPrompt);

      if (success) {
        onChatEditedEvent({
          ...payload,
          success,
          promptResponse: data?.promptResponse,
          isCompleted: true,
          promptId: data.promptId,
        });
      } else {
        setErrorMessage(
          "An unexpected error occurred. Please reload the page."
        );
      }
    }
  };

  // Alert Banner
  const fetchData = async () => {
    setFetching(true);
    try {
      const subscriptionRes = await userSubscriptionInfo();
      setActivePlan(subscriptionRes?.activePlan);
      if(subscriptionRes?.remaining?.token > 0){
        setRemainingToken(true);
      }else{
        setRemainingToken(false);
      }
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const onChatEditedEvent = (response) => {
    const {
      success,
      message,
      userPrompt,
      promptResponse,
      botProvider,
      promptId,
      threadId,
      isCompleted,
      chatLog,
    } = response;

    if (success) {
      setChatLog((prevChatLog) => {
        const index = prevChatLog.findIndex(
          (chat) => chat.promptId === promptId
        );
        if (index !== -1) {
          const newChatLog = [...prevChatLog];
          newChatLog[index] = {
            ...newChatLog[index],
            chatPrompt: userPrompt,
            botMessage: promptResponse,
          };
          return newChatLog;
        }
        // If the message is not found, just return the previous chat log
        return prevChatLog;
      });
    }
  };

  // ----- SIDE EFFECTS ----- //

  useEffect(() => {
    if (thread_id && !isFirstMessage) {
      console.log(
        "not first message and thread_id exists -> empty chat-log and fetch new chat-log"
      );
      // not first message and thread_id exists -> empty chat-log and fetch new chat-log
      setChatLog([]);
      fetchChatLogPerThread(thread_id);
    } else if (thread_id && isFirstMessage) {
      console.log(
        "is first message -> no need to fetch chat-log, thread_id has been added to url"
      );
      // is first message -> no need to fetch chat-log, thread_id has been added to url
      setIsFirstMessage(false);
    } else if (!thread_id) {
      console.log(
        "new thread created exception -> empty chat-log and set isFirstMessage to false"
      );
      // new thread created exception -> empty chat-log and set isFirstMessage to false
      setChatLog([]);
      setIsFirstMessage(false);
    }
    setCurrentThreadId(thread_id);
    // setting prompt input in focus
    if (promptInputRef.current) {
      promptInputRef.current.focus();
    }
  }, [thread_id]);

  useEffect(() => {
    const scrollableDiv = chatLogWrapperRef.current;
    if (scrollableDiv) {
      scrollableDiv.addEventListener("scroll", handleScrollToBottomButton);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener("scroll", handleScrollToBottomButton);
      }
    };
  }, [chatLogWrapperRef.current]);

  useEffect(() => {
    fetchTagList();
    fetchTemplates();

    if (promptInputRef.current) {
      inputElementAutoGrow(promptInputRef.current);
    }

    return () => {
      if (timeOutIds.length) {
        timeOutIds.forEach((id) => clearTimeout(id));
      }
    };
  }, []);
  useEffect(() => {
    if (currentPromptTemplate) {
      setInputPrompt(currentPromptTemplate);
    }

  }, [currentPromptTemplate]);

  useEffect(() => {
    inputElementAutoGrow(promptInputRef.current);
  }, [inputPrompt]);

  // [SOCKET] - setting listener for sockets
  useEffect(() => {
    const bindSocketEvents = () => {
      const chatSocket = chatSocketRef.current;
      chatSocket?.on(CHAT_EVENTS.CREATED_CHAT, onChatCreatedEvent);
      chatSocket?.on(CHAT_EVENTS.EDITED_PROMPT, onChatEditedEvent);
    };

    const unbindSocketEvents = () => {
      const chatSocket = chatSocketRef.current;
      chatSocket?.off(CHAT_EVENTS.CREATED_CHAT, onChatCreatedEvent);
      chatSocket?.off(CHAT_EVENTS.EDITED_PROMPT, onChatEditedEvent);
    };

    bindSocketEvents();
    return unbindSocketEvents;
  }, [chatSocketRef.current]);

  const onChatCreatedEvent = (response) => {
    const {
      success,
      message,
      userPrompt,
      promptResponse,
      msg_id,
      threadId,
      botProvider,
      chatLog,
      isFistThreadMessage,
      isCompleted,
      promptId,
    } = response;

    if (success) {

      // Update only the specific message
      setChatLog(prevLog => prevLog.map(msg => 
        msg.msgId === msg_id 
          ? {
              ...msg,
              chatPrompt: userPrompt,
              botMessage: promptResponse,
              msg_id,
              botProvider,
              promptId,
            }
          : msg
      ));

      // scroll to bottom
      scrollToBottomForRefElement(chatLogWrapperRef);
    } else {
      setIsGeneratingResponse(false);
      return setErrorMessage(message);
    }

    // Handle first message in thread
    if (isFistThreadMessage && isCompleted) {
      handleNewThreadException(threadId);
    }

    // Only set generating response to false when explicitly told
    if (isCompleted) {
      setIsGeneratingResponse(false);
    }
  };

  const handleNewThreadException = (threadId) => {
    console.log("[HELPER LOG] navigating to new route", threadId);
    setIsFirstMessage(true);
    // will navigate only if url is not already set with thread_id
    if (!thread_id || thread_id !== threadId) {
      navigate(`/chat/${threadId}`, { replace: true });
    }
    setTriggerNavContent((state) => state + 1);
  };

  // ----- HANDLE API CALLS ----- //
  /**
   * Handles the submission of a prompt in the chat page.
   *
   * @param {Event} e - The event object.
   * @param {boolean} [prompt=null] - If `true`, the `prompt` parameter is used as the main user prompt. Otherwise, the `inputPrompt` state is considered.
   * @returns {Promise<void>} - A promise that resolves when the submission is handled.
   */
  const handlePromptSubmit = async (e, prompt = null) => {
    e?.preventDefault();
    if (isGeneratingResponse) return;
    setShowQMassage(false);

    try {
      scrollToBottomForRefElement(chatLogWrapperRef);
      setErrorMessage(null);
      if(remaining?.token <= 0 || !remainingToken){
        setRemainingToken(false);
        return message.error("You have no remaining tokens. Please upgrade your plan.");
        
      }
      setIsGeneratingResponse(true);


      const threadId = currentThreadId ? currentThreadId : generateThreadId();
      let isFistThreadMessage = currentThreadId ? false : true;

      if (inputPrompt.trim() !== "") {
        const prompts = (prompt ?? inputPrompt).split('----').map(p => p.trim()).filter(Boolean);
        setInputPrompt("");
        promptInputRef.current.style.height = "51px";

        let previousResponse = "";
        let userInputtedPrompt = prompt ?? inputPrompt;

        for (const [index, currentPrompt] of prompts.entries()) {
          const msgId = `${new Date().getTime()}-${index}`;
          
          const enhancedPrompt = index === 0 
            ? currentPrompt 
            : `Previous response: ${previousResponse}\n\nNew prompt: ${currentPrompt}`;
          
          setChatLog(prevLog => [
            ...prevLog,
            {
              chatPrompt: currentPrompt,
              msgId,
              botProvider: selectedChatModel,
            },
          ]);

          const compid = localStorage.getItem("compId");
          const userID = localStorage.getItem("userID");
          const body = {
            threadId,
            botProvider: selectedChatModel,
            userPrompt: userInputtedPrompt,
            temp: userInputtedPrompt,
            chatLog,
            compId: compid,
            userId: userID,
            msg_id: msgId,
            isFistThreadMessage,
            isDynamicBot: selectedCompany !== null, 
            selectedCompany,
            selectedmodel: selectedModel,
          };
          console.log("Request Body:", body);

          const chatSocket = chatSocketRef.current;
          if (chatSocket) {
            await new Promise((resolve) => {
              const handleResponse = (response) => {
                if (response.msg_id === msgId) {
                  if (response.isCompleted) {
                    chatSocket.off(CHAT_EVENTS.CREATED_CHAT, handleResponse);
                    previousResponse = response.promptResponse;
                    resolve();
                  }
                  
                  setChatLog(prevLog => prevLog.map(msg => 
                    msg.msgId === msgId 
                      ? {
                          ...msg,
                          chatPrompt: currentPrompt,
                          botMessage: response.promptResponse,
                          botProvider: response.botProvider,
                          promptId: response.promptId,
                        }
                      : msg
                  ));
                }
              };
              
              chatSocket.on(CHAT_EVENTS.CREATED_CHAT, handleResponse);
              chatSocket.emit(CHAT_EVENTS.CREATE_CHAT, body);
            });
          } else {
            const { success, promptResponse, message, promptId } =
              await getGptResponse(body, cancelTokenSourceRef.current);

            if (success) {
              onChatCreatedEvent({
                ...body,
                success,
                promptResponse,
                isCompleted: true,
                promptId,
              });
            } else {
              setIsGeneratingResponse(false);
              return setErrorMessage(message);
            }
          }
        }
        
        setIsGeneratingResponse(false);
      }
    } catch (error) {
      setIsGeneratingResponse(false);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.message ||
          "Something went wrong, please reload!"
      );
    }
  };

  // ----- LOCAL HANDLERS ----- //
  const handleStopGeneratingButton = async () => {
    const chatSocket = chatSocketRef.current;
    if (chatSocket) {
      chatSocket.emit(CHAT_EVENTS.STOP_CHAT, {});
    }
    setIsGeneratingResponse(false);
  };


  const handleScrollToBottomButton = () => {
    const scrollableDiv = chatLogWrapperRef.current;

    if (scrollableDiv) {
      const isScrolledUp = scrollableDiv.scrollTop >= 0;
      const isAtBottom =
        scrollableDiv.scrollTop + scrollableDiv.clientHeight >=
        scrollableDiv.scrollHeight - 1;

      setShowScrollToBottomButton(isScrolledUp && !isAtBottom);
    }
  };

  return (
    <>
    {fetching && (
      <div className="loading-container-plans">
        <Spin size="large" />
      </div>
    )}
    <section className="chat-box">
      {/* <Typography.Title level={5} className="floating-title py-2 px-3 rounded">
				Multi-provider AI Chat
			</Typography.Title> */}
      {/* chatLogWrapper */}
      <div className={`chat-list-container ${isGeneratingResponse ? 'chat-list-container-for-generate' : ''}`} ref={chatLogWrapperRef}>
        {/* if chats loading, show skeleton */}
        {isFetchingChatLog ? (
          <ChatSkeleton />
        ) : (
          <>
            {chatLog.length === 0 ? (
              <>
                {/* ----- TEMPLATE LIST ----- */}
                <PromptTemplatesIntro
                  templateCategories={templateCategories}
                  setInputPrompt={setInputPrompt}
                />
              </>
            ) : (
              <>
                {/* ----- CHAT LIST ----- */}
                {chatLog.map((chat, idx) => (
                  <MessageContainer
                    key={chat.msg_id || idx}
                    states={{
                      chat,
                      idx,
                      loading: isGeneratingResponse,
                      error: errorMessage,
                      editProps: {
                        emitUpdateLastPrompt,
                        isLastItem: idx === chatLog.length - 1,
                      },
                    }}
                  />
                ))}
                {showScrollToBottomButton && (
                  <button
                    onClick={() =>
                      scrollToBottomForRefElement(chatLogWrapperRef)
                    }
                    className="GptScrollUpButton"
                  >
                    <FaArrowDown />
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ----- CHAT INPUT -----  */}
        <ChatPromptInputForm
          states={{
            loading: isGeneratingResponse,
            inputPrompt,
            showPromptDropdown,
            showMassage: showQMassage,
            setShowMassage: setShowQMassage,
            selectedCompany,
            selectedModel,
            setSelectedModel, 
          }}
          refs={{ promptInputRef }}
          actions={{
            onSubmit: handlePromptSubmit,
            handleStopGeneratingButton,
            setSelectedChatModel,
            selectedChatModel,
            setInputPrompt,
            setShowPromptDropdown,
            setSelectedCompany,
          }}
        />
        <WarningModal />
      </section>
    </>
  );
};

export default ChatPage;